/**
 * Copyright IBM Corp. 2022, 2026
 */

import * as React from 'react'
import Image from 'next/legacy/image'
import ReactPlayer from 'react-player'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import VideoPlayerModal from 'components/video-player-modal'
import Dialog from 'components/io-dialog'
import PlayIcon from './play-icon'
import s from './style.module.css'

export interface IoVideoBlockProps {
	videoUrl: string
	thumbnailUrl: string
	heading: string
	description: string
	person?: {
		avatarUrl: string
		name: string
		description: string
	}
}

export default function IoVideoBlock({
	videoUrl,
	thumbnailUrl,
	heading,
	description,
	person,
}: IoVideoBlockProps): React.ReactElement {
	const [showDialog, setShowDialog] = React.useState(false)
	const showVideo = () => setShowDialog(true)
	const hideVideo = () => setShowDialog(false)
	const isWistiaVideo = videoUrl.includes('wistia')
	return (
		<>
			<figure className={s.videoCallout}>
				<button className={s.thumbnail} onClick={showVideo}>
					<VisuallyHidden>Play video</VisuallyHidden>
					<PlayIcon />
					<Image src={thumbnailUrl} layout="fill" objectFit="cover" alt="" />
				</button>
				<figcaption className={s.content}>
					<h3 className={s.heading}>{heading}</h3>
					<p className={s.description}>{description}</p>
					{person && (
						<div className={s.person}>
							{person.avatarUrl ? (
								<div className={s.personAvatar}>
									<Image
										src={person.avatarUrl}
										width={52}
										height={52}
										alt={`${person.name} avatar`}
										objectFit="cover"
									/>
								</div>
							) : null}
							<div>
								<p className={s.personName}>{person.name}</p>
								<p className={s.personDescription}>{person.description}</p>
							</div>
						</div>
					)}
				</figcaption>
			</figure>
			{isWistiaVideo && showDialog ? (
				<VideoPlayerModal
					label={`${heading} video}`}
					videoUrl={videoUrl}
					onDismiss={hideVideo}
				/>
			) : null}

			{!isWistiaVideo ? (
				<Dialog
					isOpen={showDialog}
					onDismiss={hideVideo}
					label={`${heading} video}`}
				>
					<h2 className={s.videoHeading}>{heading}</h2>
					<div className={s.video}>
						<ReactPlayer
							url={videoUrl}
							width="100%"
							height="100%"
							controls
							playing
						/>
					</div>
				</Dialog>
			) : null}
		</>
	)
}
